import { useDebounceCallback, useDebounceValue } from "usehooks-ts";
import { classNames } from "@via/utils/helpers";
import { useEffect, useState } from "react";

type FilterItem = { id: string; value: unknown };

export enum SampleTrackerViewType {
  My = "my",
  Shared = "shared",
  Public = "public",
}

type SampleTrackersFilterProps = {
  columnFilters: FilterItem[];
  setColumnFilters: (
    filters: (prevFilters: FilterItem[]) => FilterItem[]
  ) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  sampleTrackerViewType?: SampleTrackerViewType;
  onSampleTrackerViewTypeChange?: (type: SampleTrackerViewType) => void;
};

const SampleTrackersFilter = ({
  columnFilters,
  setColumnFilters,
  setPage,
  setPageSize,
  sampleTrackerViewType,
  onSampleTrackerViewTypeChange,
}: SampleTrackersFilterProps) => {
  const selectedFilterClass =
    "bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md";
  const unSelectedFilterClass = "bg-white";
  const leftButtonClass = "rounded-l-md min-w-[110px]";
  const middleButtonClass = "-ml-px min-w-[150px]";
  const rightButtonClass = "rounded-r-md -ml-px min-w-[110px]";
  const defaultButtonClass =
    "relative inline-flex flex-none justify-center h-9 items-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 whitespace-nowrap";

  const [debouncedNameFilter] = useDebounceValue(
    (columnFilters.find(filter => filter.id === "name")?.value as string) || "",
    300
  );

  const [selectedViewType, setSelectedViewType] =
    useState<SampleTrackerViewType>(
      sampleTrackerViewType ?? SampleTrackerViewType.My
    );

  useEffect(() => {
    if (!sampleTrackerViewType) return;
    setSelectedViewType(sampleTrackerViewType);
  }, [sampleTrackerViewType]);

  const debounced = useDebounceCallback((id, value) => {
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === id ? { ...filter, value } : filter
      );
      if (!prevFilters.find(filter => filter.id === id) && value !== "") {
        return [...updatedFilters, { id, value }];
      }
      return updatedFilters.filter(filter => filter.value !== "");
    });
    setPage(0);
    setPageSize(10);
  }, 300);

  const handleSelectViewType = (type: SampleTrackerViewType) => {
    setSelectedViewType(type);
    onSampleTrackerViewTypeChange?.(type);
    setPage(0);
    setPageSize(10);
  };

  return (
    <div className="w-full my-2 flex flex-col items-center justify-between gap-2 md:flex-row">
      <div className="w-full flex-1">
        <input
          type="text"
          placeholder="Filter By Name"
          className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          defaultValue={debouncedNameFilter}
          onChange={e => debounced("name", e.target.value)}
        />
      </div>
      <span className="w-full isolate inline-flex rounded-md shadow-sm md:w-auto shrink-0 md:flex-none">
        <button
          type="button"
          onClick={() => handleSelectViewType(SampleTrackerViewType.My)}
          className={classNames(
            defaultButtonClass,
            leftButtonClass,
            selectedViewType === SampleTrackerViewType.My
              ? selectedFilterClass
              : unSelectedFilterClass
          )}>
          My Trackers
        </button>
        <button
          type="button"
          onClick={() => handleSelectViewType(SampleTrackerViewType.Shared)}
          className={classNames(
            defaultButtonClass,
            middleButtonClass,
            selectedViewType === SampleTrackerViewType.Shared
              ? selectedFilterClass
              : unSelectedFilterClass
          )}>
          Shared with Me
        </button>
        <button
          type="button"
          onClick={() => handleSelectViewType(SampleTrackerViewType.Public)}
          className={classNames(
            defaultButtonClass,
            rightButtonClass,
            selectedViewType === SampleTrackerViewType.Public
              ? selectedFilterClass
              : unSelectedFilterClass
          )}>
          Public
        </button>
      </span>
    </div>
  );
};

export default SampleTrackersFilter;
